import { FormEvent, ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import {
  BsEnvelope,
  BsGeoAlt,
  BsGithub,
  BsGlobe,
  BsLinkedin,
  BsX,
} from "react-icons/bs";
import { PORTFOLIO_CONTACT, PORTFOLIO_PROFILE } from "../config/portfolio-content";
import {
  CONTACT_LIMITS,
  type ContactFieldErrors,
  type ContactFieldName,
  hasContactFieldErrors,
  validateContactField,
  validateContactForm,
} from "../lib/contact-form-validation";

type ContactModalProps = {
  onClose: () => void;
};

type FormState = "idle" | "sending" | "success" | "error";

type ContactItem = {
  icon: ReactNode;
  label: string;
  value: string;
  href?: string;
  external?: boolean;
};

const CONTACT_ITEMS: ContactItem[] = [
  {
    icon: <BsEnvelope aria-hidden />,
    label: "Email",
    value: PORTFOLIO_CONTACT.email,
    href: `mailto:${PORTFOLIO_CONTACT.email}`,
  },
  {
    icon: <BsGlobe aria-hidden />,
    label: "Website",
    value: PORTFOLIO_CONTACT.website.replace(/^https?:\/\//, ""),
    href: PORTFOLIO_CONTACT.website,
    external: true,
  },
  {
    icon: <BsLinkedin aria-hidden />,
    label: "LinkedIn",
    value: "mark-jones-solano",
    href: PORTFOLIO_CONTACT.linkedin,
    external: true,
  },
  {
    icon: <BsGithub aria-hidden />,
    label: "GitHub",
    value: "mjsol12",
    href: PORTFOLIO_CONTACT.github,
    external: true,
  },
  {
    icon: <BsGeoAlt aria-hidden />,
    label: "Location",
    value: PORTFOLIO_PROFILE.location,
  },
];

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-lg);
  background: var(--resume-modal-backdrop);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
`;

const Panel = styled.div`
  width: min(96vw, 720px);
  max-height: min(92dvh, 820px);
  display: flex;
  flex-direction: column;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  box-shadow: 0 12px 40px var(--shadow-medium);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-md);
  padding: var(--space-lg) var(--space-xl);
  border-bottom: 1px solid var(--border-color);
`;

const Title = styled.h2`
  margin: 0 0 0.25rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-size: 1.75rem;
  line-height: 1;
  padding: 0.15rem;
  cursor: pointer;
  transition: color var(--transition);
  flex-shrink: 0;

  &:hover {
    color: var(--green);
  }
`;

const Body = styled.div`
  display: grid;
  gap: var(--space-xl);
  padding: var(--space-xl);
  overflow-y: auto;

  @media (min-width: 640px) {
    grid-template-columns: 1fr 1.15fr;
  }
`;

const ContactDetails = styled.div``;

const SectionTitle = styled.h3`
  margin: 0 0 var(--space-md);
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
`;

const ContactDetailsList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
`;

const ContactDetailsItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: var(--space-md);
`;

const ItemIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: var(--radius-md);
  border: 1px solid var(--border-color);
  color: var(--green);
  font-size: 1rem;
  flex-shrink: 0;
`;

const ItemContent = styled.div`
  min-width: 0;
`;

const ItemLabel = styled.span`
  display: block;
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-bottom: 2px;
`;

const ItemValue = styled.span`
  font-size: 0.875rem;
  color: var(--text-primary);
  word-break: break-word;
`;

const ItemLink = styled.a`
  font-size: 0.875rem;
  color: var(--text-primary);
  text-decoration: none;
  word-break: break-word;
  transition: color var(--transition);

  &:hover {
    color: var(--green);
  }
`;

const FormSection = styled.div``;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
`;

const FieldRow = styled.div`
  display: grid;
  gap: var(--space-md);

  @media (min-width: 480px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Field = styled.div<{ $hasError?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--text-secondary);
  }

  input,
  textarea {
    width: 100%;
    padding: 0.625rem 0.75rem;
    font-size: 0.875rem;
    font-family: inherit;
    color: var(--text-primary);
    background: var(--bg-secondary);
    border: 1px solid
      ${({ $hasError }) => ($hasError ? "#f87171" : "var(--border-color)")};
    border-radius: var(--radius-md);
    transition: border-color var(--transition);

    &:focus {
      outline: none;
      border-color: ${({ $hasError }) => ($hasError ? "#f87171" : "var(--green)")};
    }
  }

  textarea {
    resize: vertical;
    min-height: 120px;
  }
`;

const RequiredMark = styled.span`
  color: #f87171;
  margin-left: 0.15rem;
`;

const FieldHint = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const FieldError = styled.span`
  font-size: 0.75rem;
  color: #f87171;
`;

const Honeypot = styled.input`
  position: absolute;
  left: -9999px;
  opacity: 0;
  pointer-events: none;
`;

const FormError = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: #f87171;
`;

const SubmitButton = styled.button`
  align-self: flex-start;
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--navy);
  background: var(--green);
  border: 1px solid var(--green);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all var(--transition);

  &:hover:not(:disabled) {
    background: var(--white-pure);
    border-color: var(--white-pure);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  html[data-theme="light"] & {
    color: var(--white-pure);
    background: var(--navy);
    border-color: var(--navy);

    &:hover:not(:disabled) {
      background: var(--accent-readable);
      border-color: var(--accent-readable);
    }
  }
`;

const SuccessMessage = styled.p`
  margin: 0;
  padding: var(--space-md);
  font-size: 0.875rem;
  color: var(--text-primary);
  background: var(--accent-bg-hover);
  border: 1px solid var(--border-accent);
  border-radius: var(--radius-md);
  line-height: 1.6;
`;

const INITIAL_TOUCHED: Record<ContactFieldName, boolean> = {
  name: false,
  email: false,
  subject: false,
  message: false,
};

const ContactModal = ({ onClose }: ContactModalProps) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [formState, setFormState] = useState<FormState>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState<ContactFieldErrors>({});
  const [touched, setTouched] = useState(INITIAL_TOUCHED);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const showFieldError = (field: ContactFieldName) =>
    (submitAttempted || touched[field]) && Boolean(fieldErrors[field]);

  const updateField = (field: ContactFieldName, value: string) => {
    switch (field) {
      case "name":
        setName(value);
        break;
      case "email":
        setEmail(value);
        break;
      case "subject":
        setSubject(value);
        break;
      case "message":
        setMessage(value);
        break;
    }

    if (submitAttempted || touched[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        const error = validateContactField(field, value);
        if (error) next[field] = error;
        else delete next[field];
        return next;
      });
    }
  };

  const handleFieldBlur = (field: ContactFieldName, value: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      const error = validateContactField(field, value);
      if (error) next[field] = error;
      else delete next[field];
      return next;
    });
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (formState === "sending") return;

    setSubmitAttempted(true);
    setErrorMessage("");

    const values = { name, email, subject, message };
    const errors = validateContactForm(values);
    setFieldErrors(errors);
    setTouched({
      name: true,
      email: true,
      subject: true,
      message: true,
    });

    if (hasContactFieldErrors(errors)) {
      return;
    }

    setFormState("sending");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: values.name.trim(),
          email: values.email.trim(),
          subject: values.subject.trim(),
          message: values.message.trim(),
          website: honeypot,
        }),
      });

      const data = (await res.json().catch(() => ({}))) as { error?: string };

      if (!res.ok) {
        setFormState("error");
        setErrorMessage(
          data.error ?? "Something went wrong. Please try again."
        );
        return;
      }

      setFormState("success");
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
      setFieldErrors({});
      setTouched(INITIAL_TOUCHED);
      setSubmitAttempted(false);
    } catch {
      setFormState("error");
      setErrorMessage("Unable to reach the server. Please try again later.");
    }
  };

  return createPortal(
    <Backdrop
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-modal-title"
      onClick={onClose}
    >
      <Panel onClick={(e) => e.stopPropagation()}>
        <Header>
          <div>
            <Title id="contact-modal-title">Get in touch</Title>
            <Subtitle>Reach out directly or send a message below.</Subtitle>
          </div>
          <CloseButton type="button" aria-label="Close contact form" onClick={onClose}>
            <BsX />
          </CloseButton>
        </Header>

        <Body>
          <ContactDetails>
            <SectionTitle>Contact details</SectionTitle>
            <ContactDetailsList>
              {CONTACT_ITEMS.map((item) => (
                <ContactDetailsItem key={item.label}>
                  <ItemIcon>{item.icon}</ItemIcon>
                  <ItemContent>
                    <ItemLabel>{item.label}</ItemLabel>
                    {item.href ? (
                      <ItemLink
                        href={item.href}
                        {...(item.external
                          ? { target: "_blank", rel: "noopener noreferrer" }
                          : {})}
                      >
                        {item.value}
                      </ItemLink>
                    ) : (
                      <ItemValue>{item.value}</ItemValue>
                    )}
                  </ItemContent>
                </ContactDetailsItem>
              ))}
            </ContactDetailsList>
          </ContactDetails>

          <FormSection>
            <SectionTitle>Send a message</SectionTitle>
            {formState === "success" ? (
              <SuccessMessage role="status">
                Thanks — your message was sent. I&apos;ll get back to you soon.
              </SuccessMessage>
            ) : (
              <Form onSubmit={handleSubmit} noValidate>
                <FieldRow>
                  <Field $hasError={showFieldError("name")}>
                    <label htmlFor="contact-name">
                      Name
                      <RequiredMark aria-hidden>*</RequiredMark>
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={name}
                      onChange={(e) => updateField("name", e.target.value)}
                      onBlur={(e) => handleFieldBlur("name", e.target.value)}
                      required
                      aria-required="true"
                      aria-invalid={showFieldError("name")}
                      aria-describedby={
                        showFieldError("name") ? "contact-name-error" : undefined
                      }
                      maxLength={CONTACT_LIMITS.name}
                      autoComplete="name"
                    />
                    {showFieldError("name") && (
                      <FieldError id="contact-name-error" role="alert">
                        {fieldErrors.name}
                      </FieldError>
                    )}
                  </Field>
                  <Field $hasError={showFieldError("email")}>
                    <label htmlFor="contact-email">
                      Your email
                      <RequiredMark aria-hidden>*</RequiredMark>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => updateField("email", e.target.value)}
                      onBlur={(e) => handleFieldBlur("email", e.target.value)}
                      required
                      aria-required="true"
                      aria-invalid={showFieldError("email")}
                      aria-describedby={
                        showFieldError("email")
                          ? "contact-email-error"
                          : undefined
                      }
                      maxLength={CONTACT_LIMITS.email}
                      autoComplete="email"
                      inputMode="email"
                    />
                    {showFieldError("email") && (
                      <FieldError id="contact-email-error" role="alert">
                        {fieldErrors.email}
                      </FieldError>
                    )}
                  </Field>
                </FieldRow>

                <Field $hasError={showFieldError("subject")}>
                  <label htmlFor="contact-subject">
                    Subject
                    <RequiredMark aria-hidden>*</RequiredMark>
                  </label>
                  <input
                    id="contact-subject"
                    type="text"
                    name="subject"
                    value={subject}
                    onChange={(e) => updateField("subject", e.target.value)}
                    onBlur={(e) => handleFieldBlur("subject", e.target.value)}
                    required
                    aria-required="true"
                    aria-invalid={showFieldError("subject")}
                    aria-describedby={
                      showFieldError("subject")
                        ? "contact-subject-error"
                        : undefined
                    }
                    maxLength={CONTACT_LIMITS.subject}
                  />
                  {showFieldError("subject") && (
                    <FieldError id="contact-subject-error" role="alert">
                      {fieldErrors.subject}
                    </FieldError>
                  )}
                </Field>

                <Field $hasError={showFieldError("message")}>
                  <label htmlFor="contact-message">
                    Message
                    <RequiredMark aria-hidden>*</RequiredMark>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={message}
                    onChange={(e) => updateField("message", e.target.value)}
                    onBlur={(e) => handleFieldBlur("message", e.target.value)}
                    required
                    aria-required="true"
                    aria-invalid={showFieldError("message")}
                    aria-describedby={
                      showFieldError("message")
                        ? "contact-message-error"
                        : "contact-message-hint"
                    }
                    maxLength={CONTACT_LIMITS.message}
                    rows={5}
                  />
                  <FieldHint id="contact-message-hint">
                    {message.trim().length}/{CONTACT_LIMITS.message} characters
                  </FieldHint>
                  {showFieldError("message") && (
                    <FieldError id="contact-message-error" role="alert">
                      {fieldErrors.message}
                    </FieldError>
                  )}
                </Field>

                <Honeypot
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />

                {formState === "error" && errorMessage && (
                  <FormError role="alert">{errorMessage}</FormError>
                )}

                <SubmitButton type="submit" disabled={formState === "sending"}>
                  {formState === "sending" ? "Sending…" : "Send message"}
                </SubmitButton>
              </Form>
            )}
          </FormSection>
        </Body>
      </Panel>
    </Backdrop>,
    document.body
  );
};

export default ContactModal;
